<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
  type PropType,
} from '#imports';
import lottie from 'lottie-web';
import lightJson from '~/assets/lottie/light.json';
import victoryMp3Url from '~/assets/lottie/lottery-active.mp3?url';
import type { DrawGoodsVo, GoodsVO } from '~/types/auto/actuator';
import GoodsItem from './GoodsItem.vue';

const props = defineProps({
  isLimit: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: '',
  },
  isEnough: {
    type: Boolean,
    default: false,
  },
  goodsCount: {
    type: Number,
    default: 0,
  },
  goodsTotal: {
    type: Number,
    default: 0,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  goodsList: {
    type: Array as PropType<GoodsVO[]>,
    default: () => [],
  },
  goToEnoughStep: {
    type: Function,
    default: () => {},
  },
  handleClose: {
    type: Function as PropType<(event: MouseEvent) => void>,
    default: () => {},
  },
  drawSingle: {
    type: Function as PropType<(event: MouseEvent) => void>,
    default: () => {},
  },
  drawMultiple: {
    type: Function as PropType<(event: MouseEvent) => void>,
    default: () => {},
  },
  drawCount: {
    type: Number,
    default: 1,
  },
});

const lightRef = ref();
const boxOpening = ref(true);
const boxOpenComplete = ref(false);
const audioRef = ref<HTMLAudioElement>();
const lightingUp = ref(false);
const shouldRunUnpackAnimate = ref(true);
const lottieInstance = ref<any>(null);

function destroyLottie() {
  if (lottieInstance.value) {
    lottieInstance.value.destroy();
    lottieInstance.value = null;
  }
}
function showLight() {
  lottieInstance.value = lottie.loadAnimation({
    container: lightRef.value as Element,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: lightJson,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  });
}
function runLightAnimate() {
  // 显示弹窗
  destroyLottie();
  showLight();
  console.log('audioRef.value', audioRef.value);
  lightingUp.value = false;
  nextTick(() => {
    lightingUp.value = true;
  });
}

const wrapClass = computed(() => {
  if (lightingUp.value) {
    return 'light-up-animate';
  } else {
    return 'normal';
  }
});
const onUnpacked = () => {
  boxOpening.value = false;
  runLightAnimate();
};
let boxOpenTimer: ReturnType<typeof setTimeout> | null = null;
const toolbarVisible = computed(() => {
  return !boxOpening.value && !props.isEnough;
});
const onBoxOpenComplete = () => {
  boxOpenComplete.value = true;
  if (props.isEnough) {
    boxOpenTimer = setTimeout(() => {
      props.goToEnoughStep();
    }, 2000);
  }
};
const playSound = () => {
  if (!audioRef.value) return;
  audioRef.value.currentTime = 0;
  audioRef.value.play();
};
const stopSound = () => {
  if (!audioRef.value) return;
  audioRef.value.pause();
};

function skipAnimate() {
  shouldRunUnpackAnimate.value = false;
  stopSound();
}
const goodsListMaxHeight = computed(() => {
  if (props.isEnough) {
    //只减去顶栏标题
    return window.innerHeight - 68;
  }
  //减去顶栏标题和底部的按钮、关闭按钮、信息
  return window.innerHeight - 68 - (78 + 46 + 48 + 40 + 48);
});

const goodsItemHeight = computed(() => {
  const designSize = 200;
  //如果一个时直接按照设计搞高度
  if (props.drawCount === 1) {
    return designSize;
  }
  //两行，如果是抽五个时，减去标题和Gap大小信息除以2
  return Math.min((goodsListMaxHeight.value - 78 * 2 - 20) / 2, designSize);
});

const goodsListMaxWidth = computed(() => {
  const maxItemCountPerRow = 3;
  //最多两行，首行三个,按首行算，gap为20 * 2
  return (
    goodsItemHeight.value * maxItemCountPerRow + 20 * (maxItemCountPerRow - 1)
  );
});

onUnmounted(() => {
  stopSound();
  if (boxOpenTimer) {
    clearTimeout(boxOpenTimer);
    boxOpenTimer = null;
  }
});
</script>
<template>
  <div
    class="flex-center fixed bottom-0 left-0 right-0 top-0"
    :class="[wrapClass, boxOpenComplete ? '' : 'overflow-hidden']"
  >
    <div class="relative">
      <audio ref="audioRef" :src="victoryMp3Url" class="hidden"></audio>
      <div
        ref="lightRef"
        class="light pointer-events-none fixed left-0 right-0 top-0 z-[1001] mx-auto w-[800px]"
      ></div>
      <a-button
        v-if="boxOpening && goodsList.length > 0"
        type="ghost"
        size="middle"
        class="!fixed right-8 top-8 z-10 !h-[38px] min-w-[88px] border !border-[rgba(255,255,255,.5)] font-bold !text-white"
        @click="skipAnimate"
        >跳过动画</a-button
      >
      <div class="flex-center relative flex-col text-white">
        <template v-if="!boxOpening">
          <h6 class="fade-in animation-delay-500 mb-10 text-xl font-bold">
            {{ title }}
          </h6>
        </template>
        <div
          class="relative flex max-w-[664px] flex-wrap justify-center gap-5"
          :style="{
            maxHeight: goodsListMaxHeight + 'px',
            maxWidth: goodsListMaxWidth + 'px',
          }"
        >
          <GoodsItem
            v-for="(item, i) in drawCount"
            :key="i"
            :animate="shouldRunUnpackAnimate"
            :goods="goodsList[i] ? goodsList[i] : {}"
            :size="goodsItemHeight"
            @box-open="i === 0 ? onUnpacked() : undefined"
            @box-open-ok="i === 0 ? onBoxOpenComplete() : undefined"
            @play-sound="i === 0 ? playSound() : undefined"
          />
        </div>
        <template v-if="toolbarVisible">
          <div class="fade-in animation-delay-500 mt-6 flex gap-[18px]">
            <a-button
              v-login
              size="large"
              :disabled="totalCount === 0"
              class="lottery-btn !flex !h-[54px] min-w-[151px] items-center justify-center !border-white !py-0"
              type="ghost"
              @click="drawSingle"
            >
              <span class="!flex flex-col text-white">
                <i class="text-base font-bold not-italic">奇迹单抽 X1</i>
                <i v-if="isLimit" class="text-xs font-normal not-italic"
                  >抽谷机会{{ totalCount }}</i
                >
              </span>
            </a-button>
            <a-button
              v-login
              :disabled="totalCount < 5"
              size="large"
              type="primary"
              class="lottery-btn !flex !h-[54px] min-w-[151px] items-center justify-center !py-0"
              @click="drawMultiple"
            >
              <span class="!flex flex-col text-white">
                <i class="text-base font-bold not-italic">素质五连 X5</i>
                <i v-if="isLimit" class="text-xs font-normal not-italic"
                  >抽谷机会{{ totalCount }}</i
                >
              </span>
            </a-button>
          </div>

          <div class="flex-center fade-in animation-delay-500 mt-6">
            <p>本期已解锁 {{ goodsCount }} / {{ goodsTotal }}</p>
            <a-divider
              type="vertical"
              class="!top-0 !mx-4 !h-[11px] !border-[rgba(255,255,255,.25)]"
            />
            <NuxtLink
              class="flex-center"
              target="_blank"
              :to="{
                name: 'oj_home_problem',
                query: {
                  badge: 1,
                },
              }"
              ><span class="text-white">查看我的谷子</span
              ><img
                class="w-3"
                src="@/assets/image/goods-result-modal/icon-arrow.svg"
            /></NuxtLink>
          </div>
          <div
            class="fade-in animation-delay-500 absolute -bottom-12 left-1/2 -ml-5 cursor-pointer p-2"
            @click="handleClose"
          >
            <img
              class="h-6 w-6"
              src="@/assets/image/goods-result-modal/icon-close.svg"
              alt="close"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped>
.light {
  transition: opacity 0.32s;
  @apply opacity-0;
}
.normal .light,
.light-up-animate .light {
  @apply opacity-100;
}
.lottery-btn:disabled {
  @apply bg-[rgba(255,255,255,.25)] opacity-45;
}
</style>
